import { supabase } from "../config/supabase";
import {
  loadSceneFromCloud,
  saveSceneToCloud,
  CLOUD_FILE_INITIAL_VERSION,
  type SaveSceneResult,
} from "../data/cloudScene";
import { fetchThumbnailBlob, uploadThumbnail } from "../data/thumbnail";

/**
 * filesService — the single place file metadata is read from / written to.
 *
 * Every read/write of the `files` table must go through this module so that
 * later features (folders, tags, rename, duplicate, soft-delete, visibility)
 * have exactly one place to grow. No scattered `supabase.from("files")` calls
 * elsewhere in the app.
 */

export type FileVisibility = "private" | "public";

export interface FileRecord {
  id: string;
  user_id: string;
  title: string;
  visibility: FileVisibility;
  version: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

const TABLE = "files";

/**
 * Create a new file row owned by the currently signed-in user. The `id` is
 * client-generated (a uuid) and doubles as the public URL id and the R2 key.
 *
 * Upsert (not plain insert) for exactly one case: a retry-after-rollback within
 * the same dialog session (see SaveToCloudDialog#handleSave), where this id's
 * row was created moments ago and then soft-deleted by the rollback. `id` is the
 * primary key, so a plain insert would violate it; clearing `deleted_at` revives
 * that just-created row instead.
 *
 * This is NOT a general revive-or-create: the payload only sets
 * id/title/user_id/deleted_at, so reviving an OLD row would silently keep its
 * stale visibility/version/updated_at. Only call with a freshly-minted id.
 */
export const createFile = async ({
  id,
  title,
}: {
  id: string;
  title: string;
}): Promise<FileRecord> => {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const userId = session?.user?.id;
  if (!userId) {
    throw new Error("You must be signed in to save to My Files.");
  }

  const { data, error } = await supabase
    .from(TABLE)
    .upsert(
      { id, title, user_id: userId, deleted_at: null },
      { onConflict: "id" },
    )
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as FileRecord;
};

/**
 * Fetch a single (non-deleted) file row by id. RLS ensures the caller only
 * sees rows they are allowed to (their own, or public ones). Returns null when
 * no accessible row exists.
 */
export const getFile = async (id: string): Promise<FileRecord | null> => {
  const { data, error } = await supabase
    .from(TABLE)
    .select("*")
    .eq("id", id)
    .is("deleted_at", null)
    .maybeSingle();

  if (error) {
    throw error;
  }
  return (data as FileRecord) ?? null;
};

/**
 * Set a file's visibility (`private` | `public`). Owner-only: the RLS UPDATE
 * policy scopes this to the owner, so a non-owner call is rejected by the
 * database. Flipping to `public` makes `/share/<id>` viewable by anyone;
 * flipping back to `private` returns it to the uniform 404 for everyone else.
 */
export const setVisibility = async (
  id: string,
  visibility: FileVisibility,
): Promise<FileRecord> => {
  const { data, error } = await supabase
    .from(TABLE)
    .update({ visibility, updated_at: new Date().toISOString() })
    .eq("id", id)
    .is("deleted_at", null)
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as FileRecord;
};

export type FileSort = "updated" | "created" | "title";

export interface ListFilesParams {
  /** Title filter; applied server-side via a case-insensitive POSIX regex. */
  search?: string;
  /** Default: most-recently-updated first. */
  sort?: FileSort;
}

const SORT_COLUMNS: Record<FileSort, string> = {
  updated: "updated_at",
  created: "created_at",
  title: "title",
};

/**
 * Escape every POSIX regex metacharacter in a user-supplied search term, so
 * typing e.g. "." or "(a|b)" searches for those literal characters.
 *
 * Why regex and not ILIKE: postgrest-js maps `*` → `%` in like/ilike patterns
 * unconditionally, with no escape awareness — a user's `*` becomes a
 * match-everything wildcard, and pre-escaping it as `\*` just becomes `\%` (a
 * literal percent). There is no way to search for a literal `*` via ilike. The
 * POSIX-regex operators are not star-mapped, hence `imatch` below.
 *
 * This escaping is only sufficient for a single top-level filter param, as
 * used here. A rewrite into or()/and() or in.() PostgREST syntax changes
 * quote-stripping and comma/paren delimiting, and would need more escaping.
 */
const escapeRegex = (value: string): string =>
  value.replace(/[\\^$.|?*+()[\]{}]/g, (match) => `\\${match}`);

/**
 * List the signed-in user's own, non-deleted files. RLS also allows other
 * users' public files through, so this always scopes to the caller's
 * `user_id` explicitly rather than relying on RLS alone.
 */
export const listFiles = async ({
  search,
  sort = "updated",
}: ListFilesParams = {}): Promise<FileRecord[]> => {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const userId = session?.user?.id;
  if (!userId) {
    throw new Error("You must be signed in to view My Files.");
  }

  let query = supabase
    .from(TABLE)
    .select("*")
    .eq("user_id", userId)
    .is("deleted_at", null);

  const trimmedSearch = search?.trim();
  if (trimmedSearch) {
    // `imatch` (POSIX ~*) substring-matches by default, so no anchors/wildcards
    // are needed — the escaped term goes in bare. `.filter` because this
    // postgrest-js version exposes no `.imatch()` builder method.
    query = query.filter("title", "imatch", escapeRegex(trimmedSearch));
  }

  query = query.order(SORT_COLUMNS[sort], { ascending: sort === "title" });

  const { data, error } = await query;
  if (error) {
    throw error;
  }
  return (data as FileRecord[]) ?? [];
};

/**
 * Rename a file. Whitespace is trimmed; a blank title is rejected (the caller
 * — the card's inline rename UI — should catch this and revert to the prior
 * title rather than sending it).
 */
export const renameFile = async (
  id: string,
  title: string,
): Promise<FileRecord> => {
  const trimmed = title.trim();
  if (!trimmed) {
    throw new Error("Title cannot be empty.");
  }

  const { data, error } = await supabase
    .from(TABLE)
    .update({ title: trimmed, updated_at: new Date().toISOString() })
    .eq("id", id)
    .is("deleted_at", null)
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as FileRecord;
};

/**
 * Soft-delete a file (sets `deleted_at`). Never hard-deletes. RLS scopes
 * updates to the owner already; `listFiles`/`getFile` exclude soft-deleted
 * rows.
 */
export const softDeleteFile = async (id: string): Promise<FileRecord> => {
  const { data, error } = await supabase
    .from(TABLE)
    .update({ deleted_at: new Date().toISOString() })
    .eq("id", id)
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as FileRecord;
};

/**
 * Roll back an orphaned row (soft-delete it) and rethrow the original error.
 * For flows that create a row and then have a later step fail, leaving that row
 * orphaned (duplicateFile's copy row). This helper unconditionally throws, so
 * it does not suit callers that need to keep rendering UI state after a
 * rollback. Rollback failure is swallowed — nothing more to do client-side —
 * and the original error always wins.
 */
export const rollbackAndThrow = async (
  id: string,
  error: unknown,
): Promise<never> => {
  try {
    await softDeleteFile(id);
  } catch {
    // Rollback failed too; fall through and rethrow the original error.
  }
  throw error instanceof Error ? error : new Error(String(error));
};

/**
 * Duplicate a file: a new row (new id, title "<original> (copy)") plus a copy
 * of the scene blob (GET the original scene, PUT it under the new id).
 *
 * The copy is only reported as successful once the new scene blob is actually
 * written. `saveSceneToCloud` signals failure by RETURN VALUE (a non-null
 * `errorMessage` / null `version`), not by throwing, so we must inspect the
 * result: a failed PUT would otherwise leave an orphan "(copy)" row that opens
 * to a 404. On any failure the new row is rolled back (soft-deleted) and the
 * error is surfaced to the caller.
 *
 * Scene-less original: when the original's scene GET returns a genuine 404 (a
 * row exists but no blob was ever saved), the copy is created as an explicitly
 * empty-but-valid scene by PUTting an empty scene payload, so it opens as an
 * empty canvas rather than a dead 404 card. A non-404, non-200 read (transient
 * / server error) means we could NOT read the original reliably, so we roll
 * back and throw rather than fabricate a misleading empty copy.
 *
 * Image assets are NOT copied and never self-heal: the scene blob references
 * assets by the original file's id/prefix, the copy's asset store starts
 * empty, and nothing later uploads assets under the new id. A duplicated
 * file with images will show them as not-found indefinitely, until a
 * follow-up change adds asset copying here.
 */
export const duplicateFile = async (id: string): Promise<FileRecord> => {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const userId = session?.user?.id;
  const accessToken = session?.access_token;
  if (!userId || !accessToken) {
    throw new Error("You must be signed in to duplicate a file.");
  }

  const original = await getFile(id);
  if (!original) {
    throw new Error("File not found.");
  }

  const newId = crypto.randomUUID();
  const { data, error } = await supabase
    .from(TABLE)
    .insert({
      id: newId,
      title: `${original.title} (copy)`,
      user_id: userId,
    })
    .select()
    .single();

  if (error) {
    throw error;
  }
  const created = data as FileRecord;

  try {
    const { data: scene, status } = await loadSceneFromCloud(id, accessToken);

    let result: SaveSceneResult;
    if (status === 200 && scene) {
      // Copy the original's scene verbatim.
      result = await saveSceneToCloud(
        newId,
        scene.elements ?? [],
        scene.appState ?? {},
        {},
        accessToken,
        // The copy's row was just inserted: this is its first revision.
        CLOUD_FILE_INITIAL_VERSION,
      );
    } else if (status === 404 || status === 204) {
      // Original genuinely has no saved scene yet (404 legacy, 204 when the
      // row exists but the blob is missing): create the copy as an explicitly
      // empty-but-valid scene so it opens as an empty canvas.
      result = await saveSceneToCloud(
        newId,
        [],
        {},
        {},
        accessToken,
        CLOUD_FILE_INITIAL_VERSION,
      );
    } else {
      // Any other status (network 0, 5xx, malformed) means we could not read
      // the original reliably — don't fabricate a misleading empty copy.
      return await rollbackAndThrow(
        created.id,
        new Error(
          `Could not read the original scene to duplicate (status ${status}).`,
        ),
      );
    }

    // saveSceneToCloud reports failure by return value, not by throwing.
    if (result.errorMessage || result.version === null) {
      return await rollbackAndThrow(
        created.id,
        new Error(result.errorMessage || "Could not copy the scene."),
      );
    }
  } catch (copyError) {
    // A thrown error (e.g. a mocked/unexpected rejection) is handled the same
    // way as a reported failure.
    return await rollbackAndThrow(created.id, copyError);
  }

  // Best-effort: copy the thumbnail if the original has one, so the
  // duplicate doesn't show a stale placeholder until it's next saved. A
  // missing thumbnail (fetch resolves null) or any failure here is never an
  // error — it must not affect the row/scene duplication above, which has
  // already succeeded by this point.
  try {
    const thumbnailBlob = await fetchThumbnailBlob(
      id,
      original.updated_at,
      accessToken,
    );
    if (thumbnailBlob) {
      await uploadThumbnail(created.id, thumbnailBlob, accessToken);
    }
  } catch (error) {
    console.warn("Could not copy thumbnail for duplicated file", error);
  }

  return created;
};
